/**
 * @fileoverview Componente frontend móvil ultra-reactivo para aficionados en estadio: Estira y Afloja (Tug of War).
 * Dinámica interactiva de tracción masiva en tiempo real con SignalR Hub multi-sala,
 * procesamiento de micro-ráfagas de tapping a 100ms con buffer mutable, Screen WakeLock API,
 * evolución dinámica de avatares por fases de tensión (Fases 1, 2, 3), efectos hápticos y feedback visual DOM.
 * 
 * @module components/Dashboard/Sections/TugOfWarFan
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    ConnectWebSocketForTugOfWarFan,
    DisconnectWebSocketForTugOfWarFan
} from '../../../utilities/WS/TugOfWarFanWS';
import {
    GetConfig,
    SendTapBatchBL,
    RegisterFanBL,
    UnregisterFanBL,
    GetFanSessionData,
    SaveFanSessionData,
    ClearFanSessionData,
    PreloadGameAssets,
    ResolveMediaUrl,
    DeterminePhaseTier,
    CalculatePullPercentage
} from '../../../utilities/BL/TugOfWar';

/**
 * Claves de almacenamiento de sesión para recordar el bando seleccionado por el aficionado.
 * @constant {string}
 */
const SESSION_STORAGE_SIDE_KEY = 'tugofwar.selectedSide';
const SESSION_STORAGE_SIDE_KEY_ALT = 'tugofwar_selected_side';
const LOCAL_STORAGE_SIDE_KEY = 'tugofwar_player_choice';

/**
 * Intervalo de despacho en milisegundos para el buffer de taps hacia el servidor.
 * @constant {number}
 */
const BATCH_DISPATCH_INTERVAL_MILLISECONDS = 100;

/**
 * Umbral máximo de toques para forzar un flush inmediato antes del timeout.
 * @constant {number}
 */
const MAXIMUM_TAPS_PER_IMMEDIATE_BATCH = 6;

/**
 * Colección de emojis para partículas flotantes en cada toque.
 * @constant {Array<string>}
 */
const TUG_FLOATING_EMOJIS = ['⚡', '💪', '🔥', '💥', '✨', '🪢'];

/**
 * Paleta de colores y valores por defecto cuando no se ha recibido tema.
 * @constant {Object}
 */
const DEFAULT_THEME = {
    player1Name: 'EQUIPO 1',
    player1Color: '#0284c7',
    player2Name: 'EQUIPO 2',
    player2Color: '#ef4444',
    fanBackgroundColor: '#0f172a',
    fanTextColor: '#ffffff',
    fontFamily: 'Bangers'
};

/**
 * Componente principal para el Aficionado Móvil en Tug of War.
 * 
 * @function TugOfWarFan
 * @param {Object} [props] - Propiedades del componente.
 * @param {Object} [props.matchState] - Estado de la partida proveniente del router/padre.
 * @param {Object} [props.config] - Configuración o tema visual de la sala.
 * @param {string} [props.winner] - Ganador de la partida ('player1' | 'player2' | 'Empate').
 * @param {string} [props.roomId] - Identificador GUID de la sala.
 * @returns {React.ReactElement} Vista renderizada del aficionado móvil.
 */
export const TugOfWarFan = ({
    matchState: propMatchState,
    config: propConfig,
    winner: propWinner,
    roomId: propRoomId
}) => {
    // =========================================================================
    // 1. DETECCIÓN DE SALA Y BANDO DE SESIÓN
    // =========================================================================
    const searchParams = useMemo(() => {
        return typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    }, []);

    const activeRoomId = propRoomId
        || searchParams.get('roomId')
        || propMatchState?.roomId
        || (typeof localStorage !== 'undefined' ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id')) : '')
        || '';

    const normalizeSideChoice = useCallback((rawChoice) => {
        if (!rawChoice) return '';
        const normalized = String(rawChoice).toLowerCase().trim();
        if (normalized === 'player1' || normalized === 'j1' || normalized === '1') return 'player1';
        if (normalized === 'player2' || normalized === 'j2' || normalized === '2') return 'player2';
        return normalized;
    }, []);

    const [selectedPlayerSide, setSelectedPlayerSide] = useState(() => {
        const sessionData = GetFanSessionData(activeRoomId);
        if (sessionData?.playerChoice) {
            return normalizeSideChoice(sessionData.playerChoice);
        }
        if (typeof sessionStorage !== 'undefined') {
            const savedSide = sessionStorage.getItem(SESSION_STORAGE_SIDE_KEY) || sessionStorage.getItem(SESSION_STORAGE_SIDE_KEY_ALT);
            if (savedSide) return normalizeSideChoice(savedSide);
        }
        if (typeof localStorage !== 'undefined') {
            const localChoice = localStorage.getItem(LOCAL_STORAGE_SIDE_KEY);
            if (localChoice) return normalizeSideChoice(localChoice);
        }
        return '';
    });

    // =========================================================================
    // 2. ESTADOS DE RED Y CONECTIVIDAD
    // =========================================================================
    const [webSocketConnectionStatus, setWebSocketConnectionStatus] = useState('idle');

    // =========================================================================
    // 3. ESTADOS DE LA PARTIDA Y SINCRONIZACIÓN
    // =========================================================================
    const [currentEngineState, setCurrentEngineState] = useState(() => {
        const rawPhase = propMatchState?.phase || propMatchState?.state || propConfig?.phase || 'idle';
        const p = String(rawPhase).toLowerCase();
        if (p === 'playing' || p === 'running' || p === 'started') return 'playing';
        if (p === 'countdown') return 'countdown';
        if (p === 'finished' || p === 'game_over') return 'finished';
        if (p === 'paused') return 'paused';
        if (p === 'game_ready' || p === 'gameready' || p === 'scene') return 'game_ready';
        if (p === 'cover') return 'cover';
        return 'idle';
    });

    const [roundCountdownValue, setRoundCountdownValue] = useState(3);
    const [roundWinnerResult, setRoundWinnerResult] = useState(() => {
        if (propWinner) return normalizeSideChoice(propWinner) || propWinner;
        if (propMatchState?.winner) return normalizeSideChoice(propMatchState.winner) || propMatchState.winner;
        return null;
    });

    const [activeThemeData, setActiveThemeData] = useState(() => {
        return propConfig || propMatchState?.theme || null;
    });

    const [personalTapCount, setPersonalTapCount] = useState(0);

    const [telemetry, setTelemetry] = useState({
        scoreJ1: 0,
        scoreJ2: 0,
        totalTapsJ1: 0,
        totalTapsJ2: 0,
        activeFansJ1: 0,
        activeFansJ2: 0,
        pullPercentage1: 50,
        pullPercentage2: 50,
        secondsRemaining: 15,
        isSuddenDeath: false,
        ropeDisplacement: 0
    });

    // Sincronización directa con props entrantes
    useEffect(() => {
        if (propConfig && typeof propConfig === 'object') {
            setActiveThemeData((prev) => ({ ...(prev || {}), ...propConfig }));
            if (propConfig.theme) {
                setActiveThemeData((prev) => ({ ...(prev || {}), ...propConfig.theme }));
            }
        }
    }, [propConfig]);

    useEffect(() => {
        if (propMatchState) {
            const rawPhase = propMatchState.phase || propMatchState.state;
            if (rawPhase) {
                const p = String(rawPhase).toLowerCase();
                setCurrentEngineState(
                    p === 'playing' || p === 'running' || p === 'started' ? 'playing' :
                    p === 'countdown' ? 'countdown' :
                    p === 'finished' || p === 'game_over' ? 'finished' :
                    p === 'paused' ? 'paused' :
                    p === 'game_ready' || p === 'scene' ? 'game_ready' :
                    p === 'cover' ? 'cover' : 'idle'
                );
            }
            if (propMatchState.theme && typeof propMatchState.theme === 'object') {
                setActiveThemeData((prev) => ({ ...(prev || {}), ...propMatchState.theme }));
            }
            if (propMatchState.winner !== undefined && propMatchState.winner !== null) {
                setRoundWinnerResult(normalizeSideChoice(propMatchState.winner) || propMatchState.winner);
            }
        }
    }, [propMatchState, normalizeSideChoice]);

    useEffect(() => {
        if (propWinner !== undefined && propWinner !== null) {
            setRoundWinnerResult(normalizeSideChoice(propWinner) || propWinner);
            setCurrentEngineState('finished');
        }
    }, [propWinner, normalizeSideChoice]);

    // =========================================================================
    // 4. REFERENCIAS MUTABLES PARA BUFFER DE TOQUES Y WAKELOCK
    // =========================================================================
    const tapAccumulatorBufferRef = useRef({
        accumulatedTapCount: 0,
        dispatchTimerIdentifier: null
    });

    const screenWakeLockSentinelRef = useRef(null);
    const activeCombatAvatarImageRef = useRef(null);
    const particlesContainerRef = useRef(null);
    const confettiContainerRef = useRef(null);

    // =========================================================================
    // 5. GESTIÓN DEL SCREEN WAKELOCK API
    // =========================================================================
    const requestScreenWakeLock = useCallback(async () => {
        try {
            if (typeof navigator !== 'undefined' && 'wakeLock' in navigator && !screenWakeLockSentinelRef.current) {
                const wakeLockSentinel = await navigator.wakeLock.request('screen');
                screenWakeLockSentinelRef.current = wakeLockSentinel;

                wakeLockSentinel.addEventListener('release', () => {
                    screenWakeLockSentinelRef.current = null;
                });
            }
        } catch (wakeLockError) {
            // WakeLock fail-safe no bloqueante
        }
    }, []);

    const releaseScreenWakeLock = useCallback(async () => {
        try {
            if (screenWakeLockSentinelRef.current) {
                await screenWakeLockSentinelRef.current.release();
                screenWakeLockSentinelRef.current = null;
            }
        } catch (releaseError) {
            screenWakeLockSentinelRef.current = null;
        }
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'visible' && selectedPlayerSide && currentEngineState !== 'finished') {
                requestScreenWakeLock();
            }
        };

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', handleVisibilityChange);
            return () => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
            };
        }
    }, [selectedPlayerSide, currentEngineState, requestScreenWakeLock]);

    // =========================================================================
    // 6. CARGA DE CONFIGURACIÓN Y PRECARGA DE ASSETS
    // =========================================================================
    useEffect(() => {
        let isMounted = true;
        const loadInitialThemeConfiguration = async () => {
            try {
                const configurationResponse = await GetConfig(activeRoomId);
                if (isMounted && configurationResponse.success && configurationResponse.data) {
                    const theme = configurationResponse.data.activeTheme || configurationResponse.data.theme;
                    if (theme) {
                        setActiveThemeData(theme);
                        PreloadGameAssets(theme);
                    }
                    if (configurationResponse.data.currentState) {
                        const rawState = configurationResponse.data.currentState;
                        const p = String(rawState).toLowerCase();
                        if (p && p !== 'idle') {
                            setCurrentEngineState((prev) => {
                                if (prev !== 'idle') return prev;
                                return (
                                    p === 'playing' || p === 'running' || p === 'started' ? 'playing' :
                                    p === 'countdown' ? 'countdown' :
                                    p === 'finished' || p === 'game_over' ? 'finished' :
                                    p === 'paused' ? 'paused' :
                                    p === 'game_ready' || p === 'scene' ? 'game_ready' :
                                    p === 'cover' ? 'cover' : 'idle'
                                );
                            });
                        }
                    }
                }
            } catch (themeLoadingError) {
                // Fail-safe silencioso
            }
        };

        loadInitialThemeConfiguration();
        return () => {
            isMounted = false;
        };
    }, [activeRoomId]);

    // =========================================================================
    // 7. WEBSOCKET / SIGNALR: DESPACHO DE TAPS Y PROCESAMIENTO DE MENSAJES
    // =========================================================================
    const flushAccumulatedTapsToServer = useCallback(() => {
        const tapBuffer = tapAccumulatorBufferRef.current;

        if (tapBuffer.dispatchTimerIdentifier) {
            clearTimeout(tapBuffer.dispatchTimerIdentifier);
            tapBuffer.dispatchTimerIdentifier = null;
        }

        if (tapBuffer.accumulatedTapCount <= 0 || !selectedPlayerSide) {
            return;
        }

        const tapsToTransmit = tapBuffer.accumulatedTapCount;
        tapBuffer.accumulatedTapCount = 0;

        const normalizedSide = normalizeSideChoice(selectedPlayerSide);
        SendTapBatchBL(activeRoomId, normalizedSide, tapsToTransmit);
    }, [activeRoomId, selectedPlayerSide, normalizeSideChoice]);

    const handleIncomingWebSocketMessage = useCallback((receivedMessage) => {
        if (!receivedMessage || !receivedMessage.type) return;

        const normalizedType = String(receivedMessage.type).toLowerCase();
        const incomingData = receivedMessage.data || receivedMessage.payload || {};

        switch (normalizedType) {
            case 'tugofwar_snapshot':
            case 'snapshot': {
                const snapshotState = receivedMessage.state || incomingData;
                if (snapshotState.state || snapshotState.phase) {
                    const rawP = snapshotState.state || snapshotState.phase;
                    const p = String(rawP).toLowerCase();
                    setCurrentEngineState(
                        p === 'playing' || p === 'running' || p === 'started' ? 'playing' :
                        p === 'countdown' ? 'countdown' :
                        p === 'finished' || p === 'game_over' ? 'finished' :
                        p === 'paused' ? 'paused' :
                        p === 'game_ready' || p === 'scene' ? 'game_ready' :
                        p === 'cover' ? 'cover' : 'idle'
                    );
                }
                const currentTheme = snapshotState.currentTheme || snapshotState.theme;
                if (currentTheme) {
                    setActiveThemeData(currentTheme);
                    PreloadGameAssets(currentTheme);
                }
                if (snapshotState.winner) {
                    setRoundWinnerResult(normalizeSideChoice(snapshotState.winner) || snapshotState.winner);
                }
                break;
            }

            case 'tugofwar_cover_shown':
            case 'tugofwar_cover':
            case 'tugofwar.cover': {
                setCurrentEngineState('cover');
                const t = receivedMessage.state?.currentTheme || incomingData.theme || incomingData.currentTheme;
                if (t) {
                    setActiveThemeData(t);
                    PreloadGameAssets(t);
                }
                setRoundWinnerResult(null);
                break;
            }

            case 'tugofwar_game_scene_shown':
            case 'tugofwar_game_ready':
            case 'tugofwar.game_scene':
            case 'tugofwar.game_ready': {
                setCurrentEngineState('game_ready');
                const t = receivedMessage.state?.currentTheme || incomingData.theme || incomingData.currentTheme;
                if (t) {
                    setActiveThemeData(t);
                    PreloadGameAssets(t);
                }
                setRoundWinnerResult(null);
                break;
            }

            case 'tugofwar_countdown_tick':
            case 'tugofwar_countdown':
            case 'tugofwar_countdown_started':
            case 'tugofwar.countdown': {
                setCurrentEngineState('countdown');
                const countVal = receivedMessage.countdown !== undefined ? receivedMessage.countdown :
                    (receivedMessage.count !== undefined ? receivedMessage.count :
                    (receivedMessage.secondsRemaining !== undefined ? receivedMessage.secondsRemaining :
                    (incomingData.countdown !== undefined ? incomingData.countdown :
                    (incomingData.count !== undefined ? incomingData.count :
                    (incomingData.secondsRemaining !== undefined ? incomingData.secondsRemaining : 3)))));
                setRoundCountdownValue(Number(countVal) || 3);
                setRoundWinnerResult(null);
                break;
            }

            case 'tugofwar_round_started':
            case 'tugofwar_match_started':
            case 'tugofwar.started': {
                setCurrentEngineState('playing');
                setPersonalTapCount(0);
                setRoundWinnerResult(null);
                break;
            }

            case 'tugofwar_telemetry':
            case 'tugofwar.telemetry': {
                if (currentEngineState !== 'playing' && currentEngineState !== 'paused') {
                    setCurrentEngineState('playing');
                }
                const telData = incomingData.telemetry || incomingData;
                const s1 = Number(telData.scoreJ1 ?? telData.score1 ?? 0);
                const s2 = Number(telData.scoreJ2 ?? telData.score2 ?? 0);
                const pull = (typeof CalculatePullPercentage === 'function' ? CalculatePullPercentage(s1, s2) : null) || { percentage1: 50, percentage2: 50 };
                const percentage1 = pull.percentage1 !== undefined ? pull.percentage1 : 50;
                const percentage2 = pull.percentage2 !== undefined ? pull.percentage2 : 50;

                setTelemetry({
                    scoreJ1: s1,
                    scoreJ2: s2,
                    totalTapsJ1: Number(telData.totalTapsJ1 ?? telData.taps1 ?? 0),
                    totalTapsJ2: Number(telData.totalTapsJ2 ?? telData.taps2 ?? 0),
                    activeFansJ1: Number(telData.activeFansJ1 ?? telData.fans1 ?? 0),
                    activeFansJ2: Number(telData.activeFansJ2 ?? telData.fans2 ?? 0),
                    pullPercentage1: percentage1,
                    pullPercentage2: percentage2,
                    secondsRemaining: Number(telData.secondsRemaining ?? telData.timeRemaining ?? 15),
                    isSuddenDeath: Boolean(telData.isSuddenDeath || telData.wasSuddenDeathTriggered),
                    ropeDisplacement: Number(telData.ropeDisplacement ?? 0)
                });
                break;
            }

            case 'tugofwar_sudden_death_started':
            case 'tugofwar_sudden_death': {
                setTelemetry((prev) => ({ ...prev, isSuddenDeath: true }));
                break;
            }

            case 'tugofwar_paused':
            case 'tugofwar_match_paused':
            case 'tugofwar.paused': {
                setCurrentEngineState('paused');
                break;
            }

            case 'tugofwar_resumed':
            case 'tugofwar_match_resumed':
            case 'tugofwar.resumed': {
                setCurrentEngineState('playing');
                break;
            }

            case 'tugofwar_game_over':
            case 'tugofwar_match_finished':
            case 'tugofwar.finished': {
                flushAccumulatedTapsToServer();
                setCurrentEngineState('finished');
                const rawWinner = receivedMessage.winner || incomingData.winner || 'Empate';
                const finalWinner = normalizeSideChoice(rawWinner) || rawWinner;
                setRoundWinnerResult(finalWinner);
                releaseScreenWakeLock();
                break;
            }

            case 'tugofwar_match_reset':
            case 'tugofwar_reset':
            case 'tugofwar.reset': {
                flushAccumulatedTapsToServer();
                setCurrentEngineState('idle');
                setRoundWinnerResult(null);
                setPersonalTapCount(0);
                break;
            }

            default:
                break;
        }
    }, [currentEngineState, flushAccumulatedTapsToServer, normalizeSideChoice, releaseScreenWakeLock]);

    // Efecto de conexión con SignalR
    useEffect(() => {
        if (!selectedPlayerSide) {
            DisconnectWebSocketForTugOfWarFan();
            return;
        }

        ConnectWebSocketForTugOfWarFan(
            handleIncomingWebSocketMessage,
            (newStatus) => setWebSocketConnectionStatus(newStatus),
            activeRoomId,
            selectedPlayerSide
        );

        if (currentEngineState !== 'finished') {
            requestScreenWakeLock();
        }

        return () => {
            flushAccumulatedTapsToServer();
            DisconnectWebSocketForTugOfWarFan();
            releaseScreenWakeLock();

            const tapBuffer = tapAccumulatorBufferRef.current;
            if (tapBuffer.dispatchTimerIdentifier) {
                clearTimeout(tapBuffer.dispatchTimerIdentifier);
                tapBuffer.dispatchTimerIdentifier = null;
            }
        };
    }, [selectedPlayerSide, activeRoomId, handleIncomingWebSocketMessage, flushAccumulatedTapsToServer, requestScreenWakeLock, releaseScreenWakeLock, currentEngineState]);

    // =========================================================================
    // 8. EFECTOS VISUALES EN DOM: PARTICULAS, RIPPLES Y CONFETI
    // =========================================================================
    const spawnTapRippleEffect = (coordinateX, coordinateY) => {
        if (typeof document === 'undefined') return;
        const rippleElement = document.createElement('div');
        rippleElement.className = 'tugofwar-fan__ripple';
        rippleElement.style.left = `${coordinateX}px`;
        rippleElement.style.top = `${coordinateY}px`;

        const targetContainer = particlesContainerRef.current || document.body;
        targetContainer.appendChild(rippleElement);

        setTimeout(() => {
            rippleElement.remove();
        }, 340);
    };

    const spawnFloatingParticle = (coordinateX, coordinateY) => {
        if (typeof document === 'undefined') return;
        const particleElement = document.createElement('div');
        particleElement.className = 'tugofwar-fan__particle';
        particleElement.innerText = TUG_FLOATING_EMOJIS[Math.floor(Math.random() * TUG_FLOATING_EMOJIS.length)];

        const randomizedDeltaX = (Math.random() - 0.5) * 160;
        const randomizedDeltaY = -(Math.random() * 180 + 80);
        const randomizedRotation = (Math.random() - 0.5) * 90;

        particleElement.style.setProperty('--target-x', `${randomizedDeltaX}px`);
        particleElement.style.setProperty('--target-y', `${randomizedDeltaY}px`);
        particleElement.style.setProperty('--target-rotation', `${randomizedRotation}deg`);
        particleElement.style.left = `${coordinateX}px`;
        particleElement.style.top = `${coordinateY}px`;

        const targetContainer = particlesContainerRef.current || document.body;
        targetContainer.appendChild(particleElement);

        setTimeout(() => {
            particleElement.remove();
        }, 750);
    };

    // Confeti para la victoria
    useEffect(() => {
        if (currentEngineState === 'finished' && roundWinnerResult === selectedPlayerSide) {
            const container = confettiContainerRef.current;
            if (!container) return;

            const confettiColors = ['#ffd700', '#ff0055', '#00e5ff', '#39ff14', '#ffffff', '#ff9100'];
            const pieces = [];

            for (let i = 0; i < 40; i++) {
                const conf = document.createElement('div');
                conf.className = 'tugofwar-fan__confetti';
                conf.style.backgroundColor = confettiColors[Math.floor(Math.random() * confettiColors.length)];
                conf.style.left = `${Math.random() * 100}%`;
                conf.style.animationDelay = `${Math.random() * 1.5}s`;
                conf.style.animationDuration = `${Math.random() * 2 + 1.5}s`;
                container.appendChild(conf);
                pieces.push(conf);
            }

            return () => {
                pieces.forEach((p) => p.remove());
            };
        }
    }, [currentEngineState, roundWinnerResult, selectedPlayerSide]);

    // =========================================================================
    // 9. CONTROLADOR DE TAPPING ULTRA-REACTIVO
    // =========================================================================
    const handlePointerDownTap = (pointerInteractionEvent) => {
        if (currentEngineState !== 'playing' || !selectedPlayerSide) {
            return;
        }

        // Haptic feedback
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator && typeof navigator.vibrate === 'function') {
            try {
                navigator.vibrate(15);
            } catch (vibrationError) {
                // Non-blocking
            }
        }

        const touchCoordinateX = pointerInteractionEvent.clientX !== undefined
            ? pointerInteractionEvent.clientX
            : (pointerInteractionEvent.touches && pointerInteractionEvent.touches[0] ? pointerInteractionEvent.touches[0].clientX : (typeof window !== 'undefined' ? window.innerWidth / 2 : 200));

        const touchCoordinateY = pointerInteractionEvent.clientY !== undefined
            ? pointerInteractionEvent.clientY
            : (pointerInteractionEvent.touches && pointerInteractionEvent.touches[0] ? pointerInteractionEvent.touches[0].clientY : (typeof window !== 'undefined' ? window.innerHeight / 2 : 300));

        // Feedback visual
        spawnTapRippleEffect(touchCoordinateX, touchCoordinateY);
        spawnFloatingParticle(touchCoordinateX, touchCoordinateY);

        // Animación reactiva de jalón en el avatar
        if (activeCombatAvatarImageRef.current) {
            activeCombatAvatarImageRef.current.classList.add('tugofwar-fan__combat__avatar-img--pull');
            setTimeout(() => {
                if (activeCombatAvatarImageRef.current) {
                    activeCombatAvatarImageRef.current.classList.remove('tugofwar-fan__combat__avatar-img--pull');
                }
            }, 75);
        }

        // Contador personal
        setPersonalTapCount((prev) => prev + 1);

        // Buffer mutable de toques a 100ms
        const tapBuffer = tapAccumulatorBufferRef.current;
        tapBuffer.accumulatedTapCount += 1;

        if (tapBuffer.accumulatedTapCount >= MAXIMUM_TAPS_PER_IMMEDIATE_BATCH) {
            flushAccumulatedTapsToServer();
            return;
        }

        if (!tapBuffer.dispatchTimerIdentifier) {
            tapBuffer.dispatchTimerIdentifier = setTimeout(
                flushAccumulatedTapsToServer,
                BATCH_DISPATCH_INTERVAL_MILLISECONDS
            );
        }
    };

    // =========================================================================
    // 10. ACCIONES DE USUARIO: SELECCIÓN Y SALIDA
    // =========================================================================
    const handlePickSide = (chosenSide) => {
        const normalizedChoice = normalizeSideChoice(chosenSide);
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(SESSION_STORAGE_SIDE_KEY, normalizedChoice);
            sessionStorage.setItem(SESSION_STORAGE_SIDE_KEY_ALT, normalizedChoice);
        }
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(LOCAL_STORAGE_SIDE_KEY, normalizedChoice);
        }
        SaveFanSessionData(activeRoomId, normalizedChoice);
        RegisterFanBL(activeRoomId, normalizedChoice);
        setSelectedPlayerSide(normalizedChoice);
        requestScreenWakeLock();
    };

    const handleSwitchSideOrLogout = () => {
        flushAccumulatedTapsToServer();
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(SESSION_STORAGE_SIDE_KEY);
            sessionStorage.removeItem(SESSION_STORAGE_SIDE_KEY_ALT);
        }
        if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(LOCAL_STORAGE_SIDE_KEY);
        }
        ClearFanSessionData(activeRoomId);
        UnregisterFanBL(activeRoomId, '', selectedPlayerSide);
        setSelectedPlayerSide('');
        setPersonalTapCount(0);
        releaseScreenWakeLock();
        DisconnectWebSocketForTugOfWarFan();
    };

    // =========================================================================
    // 11. CÁLCULO DE EVOLUCIÓN DE AVATARES POR FASE (TIERS 1, 2, 3)
    // =========================================================================
    const isTeam1 = selectedPlayerSide === 'player1';

    const currentTeamColor = isTeam1
        ? (activeThemeData?.player1Color || DEFAULT_THEME.player1Color)
        : (activeThemeData?.player2Color || DEFAULT_THEME.player2Color);

    const currentTeamName = isTeam1
        ? (activeThemeData?.player1Name || DEFAULT_THEME.player1Name)
        : (activeThemeData?.player2Name || DEFAULT_THEME.player2Name);

    const opponentTeamName = isTeam1
        ? (activeThemeData?.player2Name || DEFAULT_THEME.player2Name)
        : (activeThemeData?.player1Name || DEFAULT_THEME.player1Name);

    // Porcentaje de fuerza de mi equipo
    const currentTeamPercentage = isTeam1 ? telemetry.pullPercentage1 : telemetry.pullPercentage2;
    const currentPhaseTier = DeterminePhaseTier(currentTeamPercentage);

    // Selección del sprite de fase correspondiente
    const currentPhaseAvatarUrl = useMemo(() => {
        if (!activeThemeData) return '';
        if (isTeam1) {
            if (currentPhaseTier === 3 && activeThemeData.player1Phase3Avatar) {
                return ResolveMediaUrl(activeThemeData.player1Phase3Avatar);
            }
            if (currentPhaseTier === 2 && activeThemeData.player1Phase2Avatar) {
                return ResolveMediaUrl(activeThemeData.player1Phase2Avatar);
            }
            return ResolveMediaUrl(activeThemeData.player1Phase1Avatar || activeThemeData.player1Avatar || '');
        } else {
            if (currentPhaseTier === 3 && activeThemeData.player2Phase3Avatar) {
                return ResolveMediaUrl(activeThemeData.player2Phase3Avatar);
            }
            if (currentPhaseTier === 2 && activeThemeData.player2Phase2Avatar) {
                return ResolveMediaUrl(activeThemeData.player2Phase2Avatar);
            }
            return ResolveMediaUrl(activeThemeData.player2Phase1Avatar || activeThemeData.player2Avatar || '');
        }
    }, [activeThemeData, isTeam1, currentPhaseTier]);

    // Avatares de selección (Fase 1)
    const team1SelectAvatarUrl = ResolveMediaUrl(activeThemeData?.player1Phase1Avatar || activeThemeData?.player1Avatar || '');
    const team2SelectAvatarUrl = ResolveMediaUrl(activeThemeData?.player2Phase1Avatar || activeThemeData?.player2Avatar || '');

    // Avatares de resultados
    const teamWinnerAvatarUrl = ResolveMediaUrl(
        isTeam1
            ? (activeThemeData?.player1WinnerAvatar || activeThemeData?.player1Winner || activeThemeData?.player1Phase1Avatar || '')
            : (activeThemeData?.player2WinnerAvatar || activeThemeData?.player2Winner || activeThemeData?.player2Phase1Avatar || '')
    );

    const teamLoserAvatarUrl = ResolveMediaUrl(
        isTeam1
            ? (activeThemeData?.player1LoserAvatar || activeThemeData?.player1Loser || activeThemeData?.player1Phase3Avatar || '')
            : (activeThemeData?.player2LoserAvatar || activeThemeData?.player2Loser || activeThemeData?.player2Phase3Avatar || '')
    );

    // =========================================================================
    // 12. RENDERIZADOR DE FONDO DINÁMICO
    // =========================================================================
    const renderFanThemeBackground = () => {
        const backgroundType = activeThemeData?.fanBackgroundType || 'color';
        const backgroundColor = activeThemeData?.fanBackgroundColor || DEFAULT_THEME.fanBackgroundColor;
        const backgroundMediaUrl = activeThemeData?.fanBackgroundUrl ? ResolveMediaUrl(activeThemeData.fanBackgroundUrl) : null;

        if (backgroundType === 'video' && backgroundMediaUrl) {
            return (
                <div className="tugofwar-fan__background">
                    <video
                        src={backgroundMediaUrl}
                        className="tugofwar-fan__background--video"
                        autoPlay
                        loop
                        muted
                        playsInline
                    />
                    <div className="tugofwar-fan__background--overlay" />
                </div>
            );
        }

        if (backgroundType === 'image' && backgroundMediaUrl) {
            return (
                <div className="tugofwar-fan__background">
                    <img
                        src={backgroundMediaUrl}
                        alt="Fondo Fan"
                        className="tugofwar-fan__background--image"
                    />
                    <div className="tugofwar-fan__background--overlay" />
                </div>
            );
        }

        return (
            <div
                className="tugofwar-fan__background"
                style={{ backgroundColor }}
            >
                <div className="tugofwar-fan__background--overlay" />
            </div>
        );
    };

    // =========================================================================
    // 13. RENDERIZADOR DE CABECERA SUPERIOR
    // =========================================================================
    const renderFanHeader = () => {
        const brandLogoUrl = activeThemeData?.brandLogoUrl ? ResolveMediaUrl(activeThemeData.brandLogoUrl) : null;
        const sponsorLogoUrl = activeThemeData?.sponsorLogoUrl ? ResolveMediaUrl(activeThemeData.sponsorLogoUrl) : null;

        return (
            <header className="tugofwar-fan__header">
                <div className="tugofwar-fan__header__logo-container">
                    {brandLogoUrl ? (
                        <img src={brandLogoUrl} alt="Logo de Marca" className="tugofwar-fan__header__logo" />
                    ) : (
                        <h1 className="tugofwar-fan__header__title">ESTIRA Y AFLOJA</h1>
                    )}
                </div>

                <div className="tugofwar-fan__header__controls">
                    {sponsorLogoUrl && (
                        <img src={sponsorLogoUrl} alt="Patrocinador" className="tugofwar-fan__header__logo tugofwar-fan__header__logo--sponsor" />
                    )}

                    {selectedPlayerSide && currentEngineState !== 'playing' && (
                        <button
                            type="button"
                            className="tugofwar-fan__header__exit-button"
                            onClick={handleSwitchSideOrLogout}
                            aria-label="Cambiar de equipo"
                        >
                            <span>Salir</span>
                        </button>
                    )}
                </div>
            </header>
        );
    };

    // =========================================================================
    // 14. VISTA: SELECCIÓN DE BANDO (EQUIPO 1 VS EQUIPO 2)
    // =========================================================================
    const renderSideSelectionScreen = () => {
        const p1Color = activeThemeData?.player1Color || DEFAULT_THEME.player1Color;
        const p2Color = activeThemeData?.player2Color || DEFAULT_THEME.player2Color;
        const p1Name = activeThemeData?.player1Name || DEFAULT_THEME.player1Name;
        const p2Name = activeThemeData?.player2Name || DEFAULT_THEME.player2Name;

        return (
            <div className="tugofwar-fan__select">
                <h2 className="tugofwar-fan__select__hero-title">ELIGE TU PERSONAJE:</h2>
                {/* <p className="tugofwar-fan__select__hero-subtitle">
                    Únete a un bando y jala la soga con toda tu fuerza junto a tu tribuna.
                </p> */}

                <div className="tugofwar-fan__select__cards-container">
                    {/* Tarjeta Equipo 1 */}
                    <div
                        className="tugofwar-fan__select__card tugofwar-fan__select__card--p1"
                        style={{ '--team-color': p1Color }}
                        onClick={() => handlePickSide('player1')}
                    >
                        {team1SelectAvatarUrl ? (
                            <img src={team1SelectAvatarUrl} alt={p1Name} className="tugofwar-fan__select__card__avatar" />
                        ) : (
                            <div className="tugofwar-fan__select__card__avatar-placeholder" style={{ backgroundColor: p1Color }}>
                                <span>💪</span>
                            </div>
                        )}
                        <div className="tugofwar-fan__select__card__info">
                            <h3 className="tugofwar-fan__select__card__team-name" style={{ color: p1Color }}>{p1Name}</h3>
                        </div>
                        <i className="bi bi-chevron-right tugofwar-fan__select__card__arrow" style={{ color: p1Color }} />
                    </div>

                    {/* Tarjeta Equipo 2 */}
                    <div
                        className="tugofwar-fan__select__card tugofwar-fan__select__card--p2"
                        style={{ '--team-color': p2Color }}
                        onClick={() => handlePickSide('player2')}
                    >
                        {team2SelectAvatarUrl ? (
                            <img src={team2SelectAvatarUrl} alt={p2Name} className="tugofwar-fan__select__card__avatar" />
                        ) : (
                            <div className="tugofwar-fan__select__card__avatar-placeholder" style={{ backgroundColor: p2Color }}>
                                <span>⚡</span>
                            </div>
                        )}
                        <div className="tugofwar-fan__select__card__info">
                            <h3 className="tugofwar-fan__select__card__team-name" style={{ color: p2Color }}>{p2Name}</h3>
                        </div>
                        <i className="bi bi-chevron-right tugofwar-fan__select__card__arrow" style={{ color: p2Color }} />
                    </div>
                </div>
            </div>
        );
    };

    // =========================================================================
    // 15. VISTA: SALA DE ESPERA
    // =========================================================================
    const renderWaitingScreen = () => {
        return (
            <div className="tugofwar-fan__waiting">
                <div
                    className="tugofwar-fan__waiting__team-badge"
                    style={{
                        backgroundColor: `${currentTeamColor}25`,
                        borderColor: currentTeamColor,
                        color: currentTeamColor
                    }}
                >
                    {currentTeamName}
                </div>

                <div className="tugofwar-fan__waiting__avatar-container">
                    <div className="tugofwar-fan__waiting__energy-ring" style={{ borderColor: currentTeamColor }} />
                    {currentPhaseAvatarUrl ? (
                        <img
                            src={currentPhaseAvatarUrl}
                            alt={currentTeamName}
                            className="tugofwar-fan__waiting__avatar"
                        />
                    ) : (
                        <div className="tugofwar-fan__waiting__avatar-placeholder" style={{ backgroundColor: currentTeamColor }}>
                            <span>🪢</span>
                        </div>
                    )}
                </div>

                <h2 className="tugofwar-fan__waiting__status-title">PREPÁRATE</h2>
                <p className="tugofwar-fan__waiting__status-text">
                    Esperando inicio de partida...
                </p>

                {/* <button
                    type="button"
                    className="tugofwar-fan__waiting__change-team-btn"
                    onClick={handleSwitchSideOrLogout}
                >
                    <i className="bi bi-arrow-left-right me-1" />
                    Cambiar de equipo
                </button> */}
            </div>
        );
    };

    // =========================================================================
    // 16. VISTA: CUENTA REGRESIVA SINCRONIZADA
    // =========================================================================
    const renderCountdownScreen = () => {
        return (
            <div className="tugofwar-fan__countdown">
                <div className="tugofwar-fan__countdown__number" key={roundCountdownValue}>
                    {roundCountdownValue}
                </div>
                <h2 className="tugofwar-fan__countdown__label">¡PREPÁRATE PARA TIRAR!</h2>
                <span className="tugofwar-fan__countdown__team-hint" style={{ color: currentTeamColor }}>
                    Equipo: {currentTeamName}
                </span>
            </div>
        );
    };

    // =========================================================================
    // 17. VISTA: COMBATE EN VIVO / TAPPING ULTRA-REACTIVO
    // =========================================================================
    const renderCombatScreen = () => {
        const phaseLabel = currentPhaseTier === 1 ? 'Fase 1: Fuerza Total'
            : currentPhaseTier === 2 ? 'Fase 2: ¡Resiste!'
            : 'Fase 3: ¡Tensión Máxima!';

        return (
            <div className="tugofwar-fan__combat">
                {/* Barra de estado superior */}
                {/* <div className="tugofwar-fan__combat__status-bar">
                    <div className="tugofwar-fan__combat__team-tag" style={{ backgroundColor: `${currentTeamColor}30`, borderColor: currentTeamColor, color: currentTeamColor }}>
                        {currentTeamName}
                    </div>

                    <div className="tugofwar-fan__combat__tap-badge">
                        <span className="tugofwar-fan__combat__tap-count">{personalTapCount}</span>
                        <span className="tugofwar-fan__combat__tap-label">TIRONES</span>
                    </div>

                    {telemetry.isSuddenDeath && (
                        <div className="tugofwar-fan__combat__sudden-death-tag">
                            ¡MUERTE SÚBITA!
                        </div>
                    )}
                </div> */}

                {/* Avatar evolucionado según fase */}
                <div className="tugofwar-fan__combat__avatar-preview">
                    {currentPhaseAvatarUrl ? (
                        <img
                            ref={activeCombatAvatarImageRef}
                            src={currentPhaseAvatarUrl}
                            alt={currentTeamName}
                            className={`tugofwar-fan__combat__avatar-img tugofwar-fan__combat__avatar-img--tier-${currentPhaseTier}`}
                        />
                    ) : (
                        <div className="tugofwar-fan__combat__avatar-placeholder" style={{ backgroundColor: currentTeamColor }}>
                            <span>💪</span>
                        </div>
                    )}
                    <span className="tugofwar-fan__combat__phase-indicator">
                        {phaseLabel}
                    </span>
                </div>

                {/* Medidor de Tracción Relativa */}
                {/* <div className="tugofwar-fan__combat__traction-meter">
                    <div className="tugofwar-fan__combat__traction-meter__bar">
                        <div
                            className="tugofwar-fan__combat__traction-meter__fill"
                            style={{
                                width: `${currentTeamPercentage}%`,
                                backgroundColor: currentTeamColor
                            }}
                        />
                    </div>
                    <div className="tugofwar-fan__combat__traction-meter__labels">
                        <span>{currentTeamName} ({currentTeamPercentage}%)</span>
                        <span>{opponentTeamName} ({100 - currentTeamPercentage}%)</span>
                    </div>
                </div> */}

                {/* Botón táctil gigante */}
                <div className="tugofwar-fan__combat__tap-zone">
                    <button
                        type="button"
                        className={`tugofwar-fan__pull-btn ${isTeam1 ? 'tugofwar-fan__pull-btn--p1' : 'tugofwar-fan__pull-btn--p2'}`}
                        style={{ '--team-color': currentTeamColor }}
                        onPointerDown={handlePointerDownTap}
                        aria-label="¡JALA LA SOGA!"
                    >
                        <span className="tugofwar-fan__pull-btn__icon">🪢</span>
                        <span className="tugofwar-fan__pull-btn__text">¡JALA!</span>
                        <span className="tugofwar-fan__pull-btn__subtext">TOCA SIN PARAR</span>
                    </button>
                    <p className="tugofwar-fan__combat__stadium-hint">
                        ¡CADA TOQUE SUMA FUERZA A TU EQUIPO!
                    </p>
                </div>

                {/* Overlay de Pausa */}
                {currentEngineState === 'paused' && (
                    <div className="tugofwar-fan__combat__pause-overlay">
                        <div className="tugofwar-fan__combat__pause-modal">
                            <i className="bi bi-pause-circle tugofwar-fan__combat__pause-icon" />
                            <h3>PARTIDA EN PAUSA</h3>
                            <p>El operador reanudará la partida en breve...</p>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // =========================================================================
    // 18. VISTA: RESULTADOS FINALES DE LA PARTIDA
    // =========================================================================
    const renderResultsScreen = () => {
        const normalizedWinner = normalizeSideChoice(roundWinnerResult);
        const normalizedSide = normalizeSideChoice(selectedPlayerSide);
        const isTie = !roundWinnerResult || roundWinnerResult === 'Empate' || roundWinnerResult === 'empate' || roundWinnerResult === 'draw' || normalizedWinner === 'empate';
        const isWinner = !isTie && normalizedWinner === normalizedSide;

        let outcomeTitle = '¡VICTORIA!';
        let outcomeDescription = 'Tu equipo ganó el estira y afloja con gran fuerza.';
        let outcomeCardModifier = 'victory';
        let outcomeAvatarUrl = teamWinnerAvatarUrl;

        if (isTie) {
            outcomeTitle = '¡EMPATE!';
            outcomeDescription = 'Ambos equipos demostraron una fuerza descomunal.';
            outcomeCardModifier = 'tie';
            outcomeAvatarUrl = teamWinnerAvatarUrl || currentPhaseAvatarUrl;
        } else if (!isWinner) {
            outcomeTitle = '¡DERROTA!';
            outcomeDescription = 'Tu equipo cayó al lodo. ¡Revancha en la próxima!';
            outcomeCardModifier = 'defeat';
            outcomeAvatarUrl = teamLoserAvatarUrl;
        }

        return (
            <div className="tugofwar-fan__results">
                <div className={`tugofwar-fan__results__card tugofwar-fan__results__card--${outcomeCardModifier}`}>
                    <h2 className={`tugofwar-fan__results__outcome-title tugofwar-fan__results__outcome-title--${outcomeCardModifier}`}>
                        {outcomeTitle}
                    </h2>
                    <p className="tugofwar-fan__results__outcome-desc">
                        {outcomeDescription}
                    </p>

                    {outcomeAvatarUrl && (
                        <img
                            src={outcomeAvatarUrl}
                            alt="Resultado"
                            className="tugofwar-fan__results__avatar"
                        />
                    )}

                    {/* <div className="tugofwar-fan__results__stats">
                        <span className="tugofwar-fan__results__stats__tap-count">{personalTapCount}</span>
                        <span className="tugofwar-fan__results__stats__tap-label">TIRONES APORTADOS</span>
                    </div> */}

                    {/* <button
                        type="button"
                        className="tugofwar-fan__results__switch-button"
                        onClick={handleSwitchSideOrLogout}
                    >
                        <i className="bi bi-arrow-left-right me-2" />
                        Cambiar de equipo / Jugar de nuevo
                    </button> */}

                    <p className="tugofwar-fan__results__next-round-hint">
                        Esperando...
                    </p>
                </div>
            </div>
        );
    };

    // =========================================================================
    // 19. RENDERIZADO PRINCIPAL
    // =========================================================================
    return (
        <main
            className="tugofwar-fan"
            style={{
                fontFamily: activeThemeData?.fontFamily || DEFAULT_THEME.fontFamily
            }}
        >
            {/* Contenedor dinámico para partículas y ondas de tap */}
            <div ref={particlesContainerRef} className="tugofwar-fan__particles-layer" />
            <div ref={confettiContainerRef} className="tugofwar-fan__confetti-layer" />

            {/* Capa de fondo temático */}
            {renderFanThemeBackground()}

            {/* Cabecera superior con logos y controles */}
            {renderFanHeader()}

            {/* Selector de bando cuando no se ha seleccionado equipo */}
            {!selectedPlayerSide && renderSideSelectionScreen()}

            {/* Vistas según el estado del motor cuando hay bando seleccionado */}
            {selectedPlayerSide && (
                <>
                    {(currentEngineState === 'idle' || currentEngineState === 'cover' || currentEngineState === 'game_ready') && renderWaitingScreen()}
                    {currentEngineState === 'countdown' && renderCountdownScreen()}
                    {(currentEngineState === 'playing' || currentEngineState === 'paused') && renderCombatScreen()}
                    {currentEngineState === 'finished' && renderResultsScreen()}
                </>
            )}

            {/* Pie de página con sala y estado de conexión */}
            <footer className="tugofwar-fan__footer">
                {/* <span>SALA: {activeRoomId ? String(activeRoomId).substring(0, 8) : 'GENERAL'}</span> */}
                {webSocketConnectionStatus === 'reconnecting' && <span className="text-warning ms-2">• Reconectando...</span>}
                {webSocketConnectionStatus === 'closed' && <span className="text-danger ms-2">• Desconectado</span>}
            </footer>
        </main>
    );
};

export default TugOfWarFan;
